'use client';

import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';

import { trackEvent } from '@/libs/analytics/events';
import { useAppDispatch } from '@/store/client/config';
import { modalActions } from '@/store/client/slices/modalSlice';
import { USE_FOOTBALL_QUERY_KEY, useBuyStickerMutation } from '@/store/server/common/football/football.query';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';
import { USE_POINT_QUERY_KEY } from '@/store/server/common/point/point.query';

interface StickerItemProps {
  stickerIdx: string;
  src: string;
  point: number;
  useYN: string;
  originalsName: OriginalsName;
  isAdded: boolean;
  onAddSticker: (idx: string, src: string) => void;
  onSelectSticker: (idx: string) => void;
  teamSlug: string;
  cardIdx: number;
}

export default function StickerItem({
  stickerIdx,
  src,
  point,
  useYN,
  originalsName,
  isAdded,
  onAddSticker,
  onSelectSticker,
  teamSlug,
  cardIdx,
}: StickerItemProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { data: initData } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });
  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const { mutate: buySticker, isPending } = useBuyStickerMutation({
    onSuccess: () => {
      trackEvent({
        name: 'using_point',
        params: {
          point: point,
          value: point,
          action_type: 'sticker',
        },
      });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getStickerList] });
      queryClient.invalidateQueries({ queryKey: [USE_POINT_QUERY_KEY.getMyPoint] });
      dispatch(
        modalActions.openAlertModal({
          icon: 'success',
          title: ['스티커 구매 완료!'],
        }),
      );
    },
    onError: () => {
      dispatch(
        modalActions.openAlertModal({
          icon: 'danger',
          title: ['구매 실패'],
          description: ['포인트가 부족하거나 오류가 발생했습니다.'],
        }),
      );
    },
  });

  const isAvailable = useYN === 'Y';

  const handleClick = () => {
    if (!isAvailable) {
      // Not purchased - show buy confirm
      if (!originalsIdx || isPending) return;
      dispatch(
        modalActions.openConfirmModal({
          title: [`${point}P를 사용하시겠습니까?`],
          description: ['구매 시 취소가 불가하니', '신중하게 확인 부탁드립니다.'],
          confirmText: ['네'],
          closeText: ['아니오'],
          onConfirm: () => {
            buySticker({ originalsIdx, stickerIdx });
            dispatch(modalActions.closeModal());
          },
        }),
      );
    } else if (isAdded) {
      // Already added - select sticker on canvas
      onSelectSticker(stickerIdx);
    } else {
      // Available and not added - add sticker
      trackEvent({ name: 'update_rep_card', params: { card_idx: cardIdx, sticker_idx: Number(stickerIdx) } });
      onAddSticker(stickerIdx, src);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative flex w-[56px] cursor-pointer flex-col items-center gap-1"
    >
      {/* Lock icon */}
      {!isAvailable && (
        <div
          className="absolute top-0 left-0 z-10 flex h-4 w-4 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
        >
          <Image
            src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_system_lock.webp`}
            alt="lock"
            width={12}
            height={12}
          />
        </div>
      )}

      {/* Image */}
      <div className="relative h-[48px] w-[48px]">
        <Image
          src={src}
          alt="sticker"
          fill
          className="object-contain"
          sizes="48px"
          style={{ opacity: isAdded ? 0.1 : 1 }}
        />

        {/* Loading spinner overlay */}
        {isPending && (
          <div className="absolute inset-0 flex items-center justify-center rounded bg-black/75">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </div>
        )}
      </div>

      {/* Status label */}
      <div
        className="flex h-[18px] max-w-[53px] items-center justify-center rounded-xs px-1 py-0.5"
        style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
      >
        {isAvailable ? (
          <span className="typo-body-8 text-white">사용 가능</span>
        ) : (
          <span className="flex items-center gap-0.5 typo-body-8 text-white">{point.toLocaleString()}P</span>
        )}
      </div>
    </button>
  );
}
